import {
  Button,
  ButtonGroup,
  Card,
  Col,
  OverlayTrigger,
  Row,
  Table,
  Tooltip
} from 'react-bootstrap'
import {
  useDeleteSurveyMutation,
  useGetSurveyQuery,
  useImportSurveyMutation,
  useUpdateStatusMutation
} from '../../store/api/survey/survey.api'
import { stringToDate } from '../../utils/date'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Pagination from '../../ui/Pagination/Pagination'
import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { searchParamsData } from '../../utils/searchParamsData'
import Loading from '../Loading/Loading'
import RemoveItemModal from '../../utils/RemoveItemModal/removeItemModal'
import { UseToggleModalHOC } from '../../hooks/toggleModal'
import { toast } from 'react-toastify'
import { IStatusUpdateData } from '../../store/api/survey/model'
import { store } from '../../store'
import { DefaultParams, EXCEL_FORMAT } from '../../utils/constants'
import { useTranslation } from 'react-i18next'
import { responseError } from '../../validation/validationRequaired'
import { CustomMessageSuccsesOrError } from '../../utils/CustomMessageSuccsesOrError'

const Surveys = (): JSX.Element => {
  const { t: tGeneral } = useTranslation('general')
  const { t: tSideBar } = useTranslation('sideBar')
  const { t: tMessages } = useTranslation('messages')
  const { isModalVisible, toggleModal } = UseToggleModalHOC()
  const [deleteSurvey] = useDeleteSurveyMutation()
  const [putChangeStatus] = useUpdateStatusMutation()
  const [importSurvey] = useImportSurveyMutation()
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>()
  const [deletedId, setDeletedId] = useState<number>(0)
  const [importLang, setImportLang] = useState<string>('en')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [searchParams, setSearchParams] = useSearchParams({
    createdBy: String(store.getState().admin.admin.id),
    ...DefaultParams
  })
  const {
    isLoading: isSurveysLoading,
    data: surveys,
    refetch
  } = useGetSurveyQuery(searchParamsData(searchParams, {}))

  useEffect(() => {
    refetch()
  }, [searchParams])

  const changeStatus = async (data: IStatusUpdateData): Promise<void> => {
    const res = await putChangeStatus(data)

    if ('error' in res) {
      responseError(res)
    } else {
      await refetch()
      toast.success(tMessages('statusUpdatedSuccessfully'))
    }
  }

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const selectedFile = event.target.files?.[0]
    const formData = new FormData()

    if (selectedFile) {
      setIsLoading(true)
      try {
        formData.append('file', selectedFile)
        const res = await importSurvey({ excelData: formData, lang: importLang })
        CustomMessageSuccsesOrError(res)
        refetch()

        event.target.value = ''
      } catch (error) {
        CustomMessageSuccsesOrError(error)
      } finally {
        setIsLoading(false)
      }
    }
  }

  return (
    <div className='mt-5'>
      {isSurveysLoading && <Loading />}
      <Row className='row-sm'>
        <Col lg={12}>
          <Card className='custom-card'>
            <Card.Header>
              <Col xs={6} md={10}>
                <h3 className='card-title mb-1'>{tSideBar('surveys')}</h3>
              </Col>
              <Col className='d-flex gap-2'>
                <div style={isLoading ? { pointerEvents: 'none' } : {}}>
                  <input
                    ref={inputRef}
                    onChange={e => handleFileChange(e)}
                    className='d-none'
                    type='file'
                    accept={EXCEL_FORMAT}
                    defaultValue={''}
                  />
                  <div className='d-flex gap-2'>
                    <div className='d-flex flex-row align-items-center gap-1'>
                      <span>isArabic</span>
                      <input
                        type='checkbox'
                        onChange={({ target }) => setImportLang(target.checked ? 'ar' : 'en')}
                      />
                    </div>
                    <button
                      type='button'
                      onClick={() => inputRef?.current.click()}
                      className='btn btn-success'
                    >
                      <span>
                        <i className='fe fe-log-in'></i>&nbsp;
                      </span>
                      {tGeneral('import')}
                    </button>
                  </div>
                </div>
                <Button onClick={() => navigate('create')}>{tGeneral('createSurvey')}</Button>
              </Col>
            </Card.Header>
            <Card.Body>
              <div className='table-responsive'>
                <Table className='table table-bordered text-nowrap text-md-nowrap mg-b-0'>
                  <thead>
                    <tr className='text-center'>
                      <th>{tGeneral('title')}</th>
                      <th>{tGeneral('createdAt')}</th>
                      <th>
                        {tGeneral('enabled')}/{tGeneral('disabled')}
                      </th>
                      <th>{tGeneral('action')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {surveys?.data?.length
                      ? surveys?.data.map((survey, index) => (
                          <tr key={index} className='text-center align-middle'>
                            <td>{survey.title}</td>
                            <td>{stringToDate(survey.created_at)}</td>
                            <td>
                              <div className='form-check form-switch'>
                                <input
                                  className='form-check-input'
                                  type='checkbox'
                                  id='flexSwitchCheckDefault'
                                  defaultChecked={Boolean(survey.enabled)}
                                  onChange={e =>
                                    changeStatus({
                                      id: survey.id,
                                      enabled: Number(e.target.checked)
                                    })
                                  }
                                />
                              </div>
                            </td>
                            <td className='w-15'>
                              <ButtonGroup size='sm' className='flex-nowrap p-1'>
                                <OverlayTrigger
                                  placement='top'
                                  overlay={<Tooltip>{tGeneral('edit')}</Tooltip>}
                                >
                                  <Button onClick={() => navigate(`edit/${survey.id}`)}>
                                    {tGeneral('edit')}
                                  </Button>
                                </OverlayTrigger>
                                <OverlayTrigger
                                  placement='top'
                                  overlay={<Tooltip>{tGeneral('delete')}</Tooltip>}
                                >
                                  <Button
                                    onClick={() => {
                                      setDeletedId(survey.id)
                                      toggleModal()
                                    }}
                                  >
                                    {tGeneral('delete')}
                                  </Button>
                                </OverlayTrigger>
                              </ButtonGroup>
                            </td>
                          </tr>
                        ))
                      : null}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
          <Pagination
            setQuery={setSearchParams}
            numPages={surveys?.count / 20}
            query={searchParams}
          />
        </Col>
      </Row>
      {isModalVisible && (
        <RemoveItemModal
          id={deletedId}
          deleteItem={deleteSurvey}
          reload={refetch}
          onClose={() => toggleModal()}
        />
      )}
    </div>
  )
}
export default Surveys
